import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { merge, Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';

export interface IRuleOption {
  id: string;
  name: string;
  entity: string;
  iconClass: string;  // e.g. 'bhi-candidate', 'bhi-person'
}

@Component({
  selector: 'app-rule-search-autocomplete',
  standalone: true,
  templateUrl: './rule-search-autocomplete.component.html',
  styleUrls: ['./rule-search-autocomplete.component.scss'],
  imports: [
    NgForOf,
    AsyncPipe,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatTooltip
  ]
})
export class RuleSearchAutocompleteComponent implements OnInit, OnChanges, OnDestroy {
  @Input() options: IRuleOption[] = [];
  @Input() entityFilter?: string;
  @Input() placeholder = 'Search all Rules...';
  @Input() existingIds: string[] = [];
  @Output() optionSelected = new EventEmitter<IRuleOption>();
  @Output() closeRequested = new EventEmitter<void>();

  searchControl = new FormControl('');
  filteredOptions$: Observable<IRuleOption[]>;
  existingIdSet = new Set<string>();
  private destroy$ = new Subject<void>();
  private existingIdsChange$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingIds']) {
      this.existingIdSet = new Set(this.existingIds ?? []);
      this.existingIdsChange$.next();
    }
  }

  ngOnInit(): void {
    this.filteredOptions$ = merge(
      this.searchControl.valueChanges.pipe(debounceTime(300)),
      this.existingIdsChange$
    ).pipe(
      startWith(null),
      map(() => this.filterOptions(typeof this.searchControl.value === 'string' ? this.searchControl.value : '')),
      takeUntil(this.destroy$)
    );
  }

  clear(): void {
    this.searchControl.setValue('');
  }

  // Keeps the input blank after selection — the rule is added to the list, not shown in the input
  displayFn(): string {
    return '';
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    if (this.searchControl.value) {
      this.clear();
    } else {
      this.closeRequested.emit();
    }
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    this.optionSelected.emit(event.option.value as IRuleOption);
    this.searchControl.setValue('', { emitEvent: false });
  }

  private filterOptions(term: string): IRuleOption[] {
    const normalised = term.toLowerCase().replace(/\s/g, '');
    return (this.options ?? [])
      .filter(opt => !this.existingIdSet.has(opt.id))
      .filter(opt => !this.entityFilter || opt.entity === this.entityFilter)
      .filter(opt => !normalised || opt.name.toLowerCase().replace(/\s/g, '').includes(normalised));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
